
import React, { useState, useRef, useEffect } from "react";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
  scripture_references?: { reference: string; text: string }[];
  recommended_tool?: { name: string; url: string };
};

const API_URL = `https://bbhhusjprzalcwvkgvsa.functions.supabase.co/standalone-chat-assistant`;

const StandaloneAssistant: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;
    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setInput("");
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: trimmed }),
      });

      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || "An error occurred.");
        setLoading(false);
        return;
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.answer,
          scripture_references: data.scripture_references,
          recommended_tool: data.recommended_tool,
        }
      ]);
    } catch (err) {
      setError("Could not connect. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="max-w-lg mx-auto py-12 px-3">
      <Card>
        <CardContent>
          <h2 className="text-2xl font-bold mb-2 text-heaven-gold">Selah Standalone Assistant</h2>
          <p className="text-sm mb-6 text-gray-600">Ask anything about faith, the Bible, or spiritual life. Responses will include relevant scripture and a recommended Selah tool to go deeper.</p>
          
          <div className="relative h-[400px] overflow-y-auto border rounded mb-4 p-3 bg-gray-50">
            {messages.length === 0 && (
              <div className="text-gray-400 text-center mt-32">Start by asking a question!</div>
            )}
            {messages.map((msg, idx) =>
              msg.role === "user" ? (
                <div key={idx} className="flex flex-row-reverse mb-2">
                  <div className="bg-heaven-gold text-white px-4 py-2 rounded-xl ml-3 max-w-[75%]">{msg.content}</div>
                </div>
              ) : (
                <div key={idx} className="mb-4">
                  <div className="bg-white border border-heaven-gold/30 rounded-xl px-4 py-3 mb-1 max-w-[95%]">
                    <div className="prose max-w-none text-gray-900" dangerouslySetInnerHTML={{ __html: msg.content.replace(/\n/g, "<br/>") }} />
                    {msg.scripture_references && msg.scripture_references.length > 0 && (
                      <div className="mt-3 bg-[#E5DEFF] p-3 rounded">
                        <span className="block text-xs uppercase font-bold text-heaven-gold mb-1">Scripture References:</span>
                        <ul>
                          {msg.scripture_references.map((ref, i) => (
                            <li key={i} className="mb-2">
                              <span className="font-semibold text-heaven-gold">{ref.reference}: </span>
                              <span className="text-gray-800">{ref.text}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {msg.recommended_tool && msg.recommended_tool.url && (
                      <div className="mt-3">
                        <a
                          href={msg.recommended_tool.url}
                          className="inline-block px-3 py-2 rounded bg-heaven-gold text-white font-semibold hover:bg-heaven-dark-gold transition"
                        >
                          {`Go to ${msg.recommended_tool.name}`}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )
            )}
            {loading && (
              <div className="flex mt-4">
                <span className="animate-pulse text-heaven-gold font-semibold">Selah is composing a response…</span>
              </div>
            )}
            {error && <div className="mt-2 text-red-600">{error}</div>}
          </div>
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              placeholder="Type your question..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleInputKeyDown}
              disabled={loading}
              className="flex-1"
            />
            <Button onClick={sendMessage} disabled={!input.trim() || loading} className="bg-heaven-gold hover:bg-heaven-dark-gold text-white">
              <Send className="h-5 w-5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
export default StandaloneAssistant;
